import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminAiKindStats, AdminOverview, AiUsageKind } from '@b4b/shared';
import { useAdminErrors } from '../../api/queries.js';
import { formatMs, formatPercent, formatUsd, healthLevel } from './format.js';

const DOT: Record<ReturnType<typeof healthLevel>, string> = {
  off: 'bg-neutral-400',
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  bad: 'bg-red-500',
};

function enabledFor(kind: AiUsageKind, e: AdminOverview['aiEnabled']): boolean {
  switch (kind) {
    case 'embedding':
      return e.embedding;
    case 'image':
      return e.images;
    case 'tags':
      return e.tags;
    case 'translation':
      return e.translation;
    case 'junk_rules':
      return true; // CLI-only, no env switch
  }
}

function ErrorList({ kind }: { kind: AiUsageKind }) {
  const { t, i18n } = useTranslation();
  const errors = useAdminErrors(kind);
  if (errors.isPending) return <p className="text-xs text-neutral-500">{t('common.loading')}</p>;
  if (!errors.data || errors.data.items.length === 0) return <p className="text-xs text-neutral-500">{t('admin.ai.noErrors')}</p>;
  return (
    <ul className="mt-2 space-y-1 text-xs">
      {errors.data.items.map((e) => (
        <li key={e.id} className="rounded-lg bg-neutral-100 p-2 font-mono dark:bg-neutral-800">
          <span className="text-neutral-500">{new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(e.createdAt))}</span>{' '}
          {e.model ? <span className="text-neutral-500">{e.model} · </span> : null}
          {e.error}
        </li>
      ))}
    </ul>
  );
}

function KindCard({ k, enabled }: { k: AdminAiKindStats; enabled: boolean }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  // junk_rules is CLI-only: off when it has never run in the range, otherwise graded on
  // failure share alone — it must not turn red just because it has been idle for 24h
  const level = k.kind === 'junk_rules' ? (k.calls === 0 ? 'off' : healthLevel(k, enabled, false)) : healthLevel(k, enabled);
  const successShare = k.calls ? (k.calls - k.failed) / k.calls : 0;
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex min-h-[44px] w-full items-center justify-between gap-3 text-left">
        <span className="flex items-center gap-2 font-semibold">
          <span aria-hidden className={`inline-block h-2.5 w-2.5 rounded-full ${DOT[level]}`} />
          {t(`admin.ai.kind.${k.kind}`)}
        </span>
        <span className="text-xs text-neutral-500">{t(`admin.ai.level.${level}`)}</span>
      </button>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-neutral-500">{t('admin.ai.calls')}</dt>
          <dd>{k.calls}</dd>
        </div>
        <div>
          <dt className="text-xs text-neutral-500">{t('admin.ai.success')}</dt>
          <dd>{k.calls ? formatPercent(successShare, i18n.language) : '–'}</dd>
        </div>
        <div>
          <dt className="text-xs text-neutral-500">{t('admin.ai.cost')}</dt>
          <dd>{formatUsd(k.costUsd, i18n.language)}</dd>
        </div>
        <div>
          <dt className="text-xs text-neutral-500">{t('admin.ai.latency')}</dt>
          <dd>{formatMs(k.avgDurationMs)}</dd>
        </div>
      </dl>
      {k.lastError ? (
        <p className="mt-2 truncate text-xs text-red-600 dark:text-red-400" title={k.lastError.message}>
          {t('admin.ai.lastError')}: {k.lastError.message}
        </p>
      ) : null}
      {open ? <ErrorList kind={k.kind} /> : null}
    </section>
  );
}

export default function AiKindCards({ overview }: { overview: AdminOverview }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {overview.ai.map((k) => (
        <KindCard key={k.kind} k={k} enabled={enabledFor(k.kind, overview.aiEnabled)} />
      ))}
    </div>
  );
}
